/**
 * Component for creating, editing, duplicating, and deleting application roles.
 *
 * @module components/admin/Roles
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, { useState, useEffect } from 'react';
import {
    Box,
    FormControl,
    Grid,
    makeStyles,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    Typography,
    withStyles,
    Paper,
    Button,
    InputBase,
    MuiThemeProvider,
    Tooltip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import { createTheme } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Alert } from '@material-ui/lab';
import { deleteRole } from '../../services/result.service';
import { fetchCompany, fetchCompanyRoles, saveRole } from '../../redux/admin/action';
import AdminsPermissions from '../../permissions/admin';
import { permissionCheck } from '../../utils/permissionCheck';
import RolesStyle from './Roles';

/**
 * A global theme configuration object used to define the visual appearance of an application.
 * It customizes color palettes and typography styles for consistent UI design.
 *
 * Properties:
 * - palette: Configures the main color scheme for the theme.
 *   - primary: Defines the primary color palette with attributes for main, light, dark, and contrast text colors.
 *   - secondary: Defines the secondary color palette with attributes for main, light, dark, and contrast text colors.
 * - typography: Configures font-related specifications such as the font family.
 */
const globalTheme = createTheme({
    palette: {
        primary: {
            main: '#832544',
            light: '#832544',
            dark: '#832544',
            contrastText: '#fff',
        },
        secondary: {
            main: '#264172',
            light: '#264172',
            dark: '#264172',
            contrastText: '#000000',
        },
    },
    typography: {
        fontFamily: 'Proxima Nova',
    },
});

/**
 * StyledSwitch is a customized Material-UI Switch component with additional styles.
 *
 * This component is styled using the `withStyles` higher-order component to provide a more customized UI,
 * with styling applied to the root, switchBase, thumb, track, and other subcomponents.
 *
 * The component supports theme-based spacing, color adjustments, and transitions for enhanced visual feedback.
 * It includes custom styling for states such as `checked` and `focusVisible`, allowing for a tailored look and feel.
 *
 * Properties like `focusVisibleClassName` and `disableRipple` are used to further enhance accessibility and behavior.
 *
 * Classes applied:
 * - `root`: Base styling for the switch container.
 * - `switchBase`: Base styling for the interactive switch control.
 * - `thumb`: Styling for the circular thumb of the switch.
 * - `track`: Styling for the track of the switch.
 * - `checked`: Additional styling when the switch is in the "checked" state.
 * - `focusVisible`: Styling for the switch in a focused state.
 */
const StyledSwitch = withStyles((theme) => ({
    root: {
        width: '2em',
        height: '1.2em',
        padding: 0,
        marginRight: theme.spacing(1),
        marginLeft: theme.spacing(1),
    },
    switchBase: {
        padding: 2,
        '&$checked': {
            transform: 'translateX(11px)',
            color: theme.palette.common.white,
            '& + $track': {
                backgroundColor: '#0E8F66',
                opacity: 1,
                border: 'none',
            },
        },
        '&$focusVisible $thumb': {
            color: '#52d869',
            border: '6px solid #fff',
        },
    },
    thumb: {
        width: '0.5em',
        height: '0.5em',
    },
    track: {
        borderRadius: 26 / 2,
        border: `1px solid ${theme.palette.grey[400]}`,
        backgroundColor: '#D8D8D8',
        opacity: 1,
        transition: theme.transitions.create(['background-color', 'border']),
    },
    checked: {},
    focusVisible: {},
}))(({ classes, ...props }) => (
    <Switch
        focusVisibleClassName={classes.focusVisible}
        disableRipple
        classes={{
            root: classes.root,
            switchBase: classes.switchBase,
            thumb: classes.thumb,
            track: classes.track,
            checked: classes.checked,
        }}
        {...props}
    />
));

/**
 * Roles component that manages and handles various operations related to roles.
 *
 * This component provides functionalities for viewing, creating, editing, copying,
 * enabling/disabling, and deleting roles associated with companies. It also manages
 * UI states and displays an alert mechanism for error notifications.
 *
 * @param {Object} dependencies - The dependencies required by the component.
 * @param {Array} dependencies.companyDetails - List of company details used for populating dropdown options.
 * @param {Function} dependencies.fetchCompany - Function to fetch company details when the component is initialized.
 * @param {Function} dependencies.fetchCompanyRoles - Function to fetch roles associated with a company.
 * @param {Array} dependencies.rolesCompany - Array of roles associated with the selected company.
 * @param {Function} dependencies.saveRole - Function to save or update a role.
 */
const Roles = ({
    companyDetails,
    fetchCompany,
    fetchCompanyRoles,
    rolesCompany,
    saveRole,
}) => {
    const useStyles = makeStyles((theme) => RolesStyle(theme));
    const classes = useStyles();
    const [companyName, setCompanyName] = useState('Select');
    const [role, setRole] = useState('');
    const [company, setCompany] = useState('Select');
    const [update, setUpdate] = useState(false);
    const [id, setId] = useState('');
    const [open, setOpen] = useState(false);
    const [item, setItem] = useState({});
    const [deleteOpenDialog, setDeleteOpenDialog] = useState(false);
    const [openAlert, setOpenAlert] = React.useState(false);
    const [alertMesage, setAlertMessage] = React.useState('');
    const [disableSave, setDisableSave] = React.useState(false);

    useEffect(() => {
        fetchCompany();
    }, [fetchCompany]);

    /**
     * Handles the company selection process by fetching the roles associated with the company
     * and updating the company name state accordingly.
     *
     * @param {Event} e - The event triggered by the company selection input. The event object
     *                     should contain the target value representing the selected company name.
     */
    const handleCompany = (e) => {
        fetchCompanyRoles(e.target.value);
        setCompanyName(e.target.value);
    };

    // Method to save or edit roles
    /**
     * Handles the process of saving a role and updating relevant state.
     *
     * This asynchronous function triggers the saving of a role with the specified
     * data. If an ID is present, it includes the ID in the data payload to update
     * an existing role. After saving, it optionally fetches roles for a specific
     * company if the selected company name is valid. Finally, it resets the related
     * UI state.
     *
     * - Disables the save action while the operation is in progress.
     * - Constructs the data payload with role name, company ID, and optionally, the role ID.
     * - Calls the `saveRole` function to persist the role data.
     * - If a company name is selected, triggers `fetchCompanyRoles` to update the list of roles.
     * - Resets form states for company, role name, and role ID after operation completes.
     *
     * @async
     * @function
     * @throws Will propagate errors from underlying asynchronous operations.
     */
    const onSaveRole = async () => {
        setDisableSave(true);
        const data = {
            name: role,
            company_id: company,
        };
        if (id) {
            data._id = id;
        }
        await saveRole(data);
        if (companyName !== 'Select') {
            await fetchCompanyRoles(companyName);
        }
        setCompany('Select');
        setRole('');
        setId('');
    };

    // Method to Enable or disable the roles
    /**
     * Handles the disabling or enabling of a role for a company.
     *
     * This asynchronous function takes an item object, modifies its `disabled`
     * status, and updates the role information in the database. It then fetches
     * the updated list of roles associated with the specified company.
     *
     * @param {Object} item - The role object to be updated.
     * @param {string} item._id - The unique identifier of the role.
     * @param {string} item.company_id - The identifier of the associated company.
     * @param {boolean} item.disabled - The current disabled status of the role.
     *
     * @returns {Promise<void>} Resolves when the role is successfully updated
     * and the updated company roles are fetched.
     */
    const onDisable = async (item) => {
        const data = {
            _id: item._id,
            company_id: item.company_id,
            disabled: !item.disabled,
        };
        await saveRole(data);
        await fetchCompanyRoles(companyName);
    };

    // Method to fill the input fields for edit
    /**
     * Edits the properties of a role by updating the respective state variables.
     *
     * The function updates the following state values:
     * - Enables the save functionality by disabling any save restrictions.
     * - Sets the role name based on the provided item's name.
     * - Assigns the associated company identifier from the provided item's company information.
     * - Updates the role's unique identifier.
     * - Sets the update flag to true, indicating that changes are being made.
     *
     * @param {Object} item - The item representing the role to be edited.
     * @param {string} item.name - The name of the role.
     * @param {string} item.company_id - The ID of the associated company.
     * @param {string} item._id - The unique identifier of the role.
     */
    const editRole = (item) => {
        setDisableSave(false);
        setRole(item.name);
        setCompany(item.company_id);
        setId(item._id);
        setUpdate(true);
    };

    // Method to cancel the edit
    /**
     * Resets the state values to their initial defaults and cancels any ongoing operations.
     *
     * The `cancel` function is typically used to revert changes made in a form or component
     * by resetting state variables to their default values. This includes enabling the save button,
     * clearing selected role and ID values, and resetting the company selection to its default option.
     */
    const cancel = () => {
        setDisableSave(false);
        setUpdate(false);
        setRole('');
        setId('');
        setCompany('Select');
    };

    // Method to open Dialog box for clone
    /**
     * Handles the copy action by updating the state with the provided item and opening a dialog or modal.
     *
     * @function handleCopy
     * @param {any} item - The data item to be copied or processed.
     * @returns {void}
     */
    const handleCopy = (item) => {
        setOpen(true);
        setItem(item);
    };

    // Method to close Dialog box
    /**
     * A callback function to handle the closing of a component or dialog.
     * It performs the following actions:
     * - Sets the state variable `open` to `false`, typically to hide or close the component.
     * - Resets the `item` state to an empty object.
     */
    const handleClose = () => {
        setOpen(false);
        setItem({});
    };

    // Method to duplicate the role
    /**
     * Handles the duplication of an item, creating a new item based on the original item and saving it.
     * The new item has the name with a "(Copy)" suffix and all other relevant properties copied from the original item.
     * If a specific company name other than 'Select' is provided, fetches the roles for that company after saving the duplicate.
     * Finally, closes the open dialog or component by updating the state.
     *
     * @async
     * @function duplicateConfirm
     * @returns {Promise<void>} A promise that resolves when the duplication and subsequent operations are completed.
     */
    const duplicateConfirm = async () => {
        const data = {
            name: `${item.name}(Copy)`,
            company_id: item.company_id,
            duplicate_from: item._id,
        };
        await saveRole(data);
        if (companyName !== 'Select') {
            await fetchCompanyRoles(companyName);
        }
        setOpen(false);
    };

    // Method to close delete dialog bix
    /**
     * Handles the closing of the delete confirmation dialog.
     *
     * This function is responsible for setting the `deleteOpenDialog` state to `false`,
     * effectively closing the dialog associated with the delete action.
     */
    const handleDeleteClose = () => {
        setDeleteOpenDialog(false);
    };

    // Method to delete role
    /**
     * Handles the confirmation for deleting a role.
     * This function performs the following actions:
     * - Closes the delete confirmation dialog.
     * - Sends a request to delete a role using the provided role ID.
     * - Displays an alert message if the deletion fails.
     * - Fetches updated roles for the selected company if applicable.
     * - Resets the selected item state after deletion.
     *
     * @async
     * @function deleteConfirm
     * @returns {Promise<void>} A promise that resolves once the operation is complete.
     */
    const deleteConfirm = async () => {
        setDeleteOpenDialog(false);
        const deleteStatus = await deleteRole({ id: item._id });
        if (!deleteStatus.data.success) {
            setAlertMessage(deleteStatus.data.message);
            setOpenAlert(true);
        }
        setDeleteOpenDialog(false);
        if (companyName !== 'Select') {
            await fetchCompanyRoles(companyName);
        }
        setItem({});
    };

    // Method to open delete dialog
    /**
     * A function that handles the action of preparing for an item deletion.
     * It sets the state to open a confirmation dialog and stores the item to be deleted.
     *
     * @function handleDelete
     * @param {Object} item - The item object that is intended to be deleted.
     * @returns {void}
     */
    const handleDelete = (item) => {
        setDeleteOpenDialog(true);
        setItem(item);
    };

    /**
     * Handles the close event for an alert component.
     *
     * This function is triggered when an alert is requested to close. It prevents
     * the alert from closing if the reason for the close event is 'clickaway'.
     * Otherwise, it proceeds to close the alert and resets its message.
     *
     * @param {object} event - The event object associated with the close action.
     * @param {string} reason - The reason for the close action. Can be used to
     *                          determine if the alert should remain open.
     */
    const handleAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenAlert(false);
        setAlertMessage('');
    };

    return (
        <div>
            <MuiThemeProvider theme={globalTheme}>
                {openAlert ? (
                    <Alert
                        variant="filled"
                        severity="error"
                        onClose={handleAlertClose}
                        className={classes.errorAlert}
                    >
                        {alertMesage}
                    </Alert>
                ) : null}
                <Grid container direction="row" spacing={3}>
                    <Grid item xs={2} />
                    <Grid item xs={4}>
                        <Typography
                            variant="subtitle1"
                            align="left"
                            className={`${classes.boldFont} ${classes.roleGrid}`}
                        >
                            <Box mb={0.5}>Roles</Box>
                            <Box>
                                <FormControl
                                    variant="outlined"
                                    size="small"
                                    style={{ width: '100%' }}
                                >
                                    <Select
                                        labelId="select-role-type"
                                        id="simple-select-role"
                                        fullWidth
                                        value={companyName}
                                        onChange={handleCompany}
                                        className={classes.select}
                                    >
                                        <MenuItem value="Select" selected>
                                            Select Company
                                        </MenuItem>
                                        {companyDetails.map((ele) => (
                                            <MenuItem key={ele._id} value={ele._id}>
                                                {ele.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TableContainer
                                    component={Paper}
                                    className={`${classes.root} ${classes.transprentBackground}`}
                                >
                                    <Table aria-label="sims table">
                                        <TableHead className={classes.thead}>
                                            <TableRow>
                                                <TableCell className={`${classes.heading} ${classes.headingBorder}`}>ROLE</TableCell>
                                                <TableCell padding="none" className={classes.heading}>
                                                    ENABLE
                                                </TableCell>
                                                <TableCell
                                                    padding="none"
                                                    className={`${classes.heading} ${classes.textRight}`}
                                                >
                                                    ACTION
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell className={classes.pySmall} />
                                                <TableCell className={classes.pySmall} />
                                                <TableCell className={classes.pySmall} />
                                            </TableRow>
                                            {rolesCompany.map((item) => (
                                                <TableRow key={item._id}>
                                                    <TableCell
                                                        className={`${classes.bodyCell} ${classes.leftTdRadius}`}
                                                    >
                                                        {item.name}
                                                    </TableCell>

                                                    <TableCell
                                                        padding="none"
                                                        className={classes.bodyCell}
                                                    >
                                                        <Tooltip
                                                            title={
                                                                permissionCheck(
                                                                    AdminsPermissions.roles.update.task,
                                                                    AdminsPermissions.roles.update.type,
                                                                ) ? '' : "You don't have permission to enable/disable the roles"
                                                            }
                                                            placement="top"
                                                        >
                              <span>
                                <StyledSwitch
                                    color="primary"
                                    onClick={() => onDisable(item)}
                                    className={permissionCheck(
                                        AdminsPermissions.roles.update.task,
                                        AdminsPermissions.roles.update.type,
                                    ) ? classes.enableSwitch : classes.disableSwitch}
                                    checked={!item.disabled}
                                />
                              </span>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell
                                                        padding="none"
                                                        className={`${classes.bodyCell} ${classes.rightTdRadius} ${classes.textRight}`}
                                                    >
                                                        <Box component="span" mr={1}>
                                                            <Tooltip title="Edit" placement="top">
                                                                <EditIcon
                                                                    style={{
                                                                        background: '#E2EAF9',
                                                                        padding: '.2rem',
                                                                        cursor: 'pointer',
                                                                    }}
                                                                    color="secondary"
                                                                    fontSize="small"
                                                                    onClick={() => editRole(item)}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip title="Duplicate" placement="top">
                                                                <FileCopyIcon
                                                                    style={{
                                                                        background: '#E2EAF9',
                                                                        padding: '.2rem',
                                                                        margin: '0 .3rem',
                                                                        cursor: 'pointer',
                                                                    }}
                                                                    color="secondary"
                                                                    fontSize="small"
                                                                    onClick={() => handleCopy(item)}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip title="Delete" placement="top">
                                                                <DeleteForeverIcon
                                                                    style={{
                                                                        background: '#E2EAF9',
                                                                        padding: '.2rem',
                                                                        cursor: 'pointer',
                                                                    }}
                                                                    color="secondary"
                                                                    fontSize="small"
                                                                    onClick={() => handleDelete(item)}
                                                                />
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Typography>
                    </Grid>

                    <Grid item xs={4}>
                        <Typography
                            variant="subtitle1"
                            align="left"
                            className={`${classes.boldFont} ${classes.addRoleGrid}`}
                        >
                            <Box mb={0.5}>Add Role</Box>
                            <Box>
                                <div className={classes.search}>
                                    <InputBase
                                        placeholder="Enter Role"
                                        classes={{
                                            root: classes.inputRoot,
                                            input: classes.inputInput,
                                        }}
                                        value={role}
                                        onChange={(e) => setRole(e.target.value)}
                                    />
                                </div>
                                <FormControl
                                    variant="outlined"
                                    size="small"
                                    style={{ width: '100%' }}
                                >
                                    <Box my={3} style={{ border: '1px #E8E2E4 solid' }} />
                                    <Box mb={1}>
                                        <Typography variant="body2" component="h3">
                                            Select Company
                                        </Typography>
                                    </Box>
                                    <Select
                                        labelId="select-company-type"
                                        id="simple-select-company"
                                        fullWidth
                                        value={company}
                                        onChange={(e) => setCompany(e.target.value)}
                                        className={classes.select}
                                    >
                                        <MenuItem value="Select" selected>
                                            Select
                                        </MenuItem>
                                        {companyDetails.map((ele) => (
                                            <MenuItem key={ele._id} value={ele._id}>
                                                {ele.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <Box my={2}>
                                        {!update
                                            ? (
                                                <Tooltip
                                                    title={
                                                        permissionCheck(
                                                            AdminsPermissions.roles.create.task,
                                                            AdminsPermissions.roles.create.type,
                                                        ) ? '' : "You don't have permission to create new roles"
                                                    }
                                                    placement="top-end"
                                                    style={{ float: 'right' }}
                                                >
                          <span>
                            <Button
                                variant="contained"
                                size="small"
                                color="primary"
                                className={permissionCheck(
                                    AdminsPermissions.roles.create.task,
                                    AdminsPermissions.roles.create.type,
                                ) ? classes.addRoleButton : classes.disableAddBtn}
                                disableElevation
                                disabled={disableSave || (role === '' && companyName === 'Select')}
                                onClick={onSaveRole}
                            >
                              Save
                            </Button>
                          </span>
                                                </Tooltip>
                                            )
                                            : (
                                                <>
                                                    <Tooltip
                                                        title={
                                                            permissionCheck(
                                                                AdminsPermissions.roles.update.task,
                                                                AdminsPermissions.roles.update.type,
                                                            ) ? '' : "You don't have permission to update"
                                                        }
                                                        placement="top-end"
                                                        style={{ float: 'right' }}
                                                    >
                            <span>
                              <Button
                                  className={permissionCheck(
                                      AdminsPermissions.roles.update.task,
                                      AdminsPermissions.roles.update.type,
                                  ) ? classes.addRoleButton : classes.disableAddBtn}
                                  variant="contained"
                                  color="primary"
                                  disabled={disableSave}
                                  onClick={onSaveRole}
                              >
                                Update
                              </Button>
                            </span>
                                                    </Tooltip>
                                                    <Button
                                                        variant="contained"
                                                        className={classes.cancel}
                                                        disableElevation
                                                        onClick={cancel}
                                                    >
                                                        CANCEL
                                                    </Button>
                                                </>
                                            )}
                                    </Box>

                                </FormControl>
                            </Box>
                        </Typography>
                    </Grid>

                    <Grid item xs={3} />
                </Grid>
                <Dialog
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">Duplicate Role</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Are you sure you want to duplicate this role
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleClose}
                            className={classes.cancelButton}
                        >
                            Cancel
                        </Button>
                        <Tooltip
                            title={
                                permissionCheck(
                                    AdminsPermissions.roles.create.task,
                                    AdminsPermissions.roles.create.type,
                                ) ? '' : "You don't have permission to duplicate"
                            }
                            placement="top-end"
                            style={{ float: 'right' }}
                        >
              <span>
                <Button
                    onClick={() => duplicateConfirm()}
                    autoFocus
                    className={permissionCheck(
                        AdminsPermissions.roles.create.task,
                        AdminsPermissions.roles.create.type,
                    ) ? classes.duplicate : classes.disableDuplicate}
                >
                  Duplicate
                </Button>
              </span>
                        </Tooltip>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={deleteOpenDialog}
                    onClose={handleDeleteClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">Delete Role</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Are you sure want to delete this role
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleDeleteClose}
                            className={classes.cancelButton}
                        >
                            Cancel
                        </Button>

                        <Tooltip
                            title={
                                permissionCheck(
                                    AdminsPermissions.roles.delete.task,
                                    AdminsPermissions.roles.delete.type,
                                ) ? '' : "You don't have permission to delete"
                            }
                            placement="top-end"
                            style={{ float: 'right' }}
                        >
              <span>
                <Button
                    onClick={() => deleteConfirm()}
                    autoFocus
                    className={permissionCheck(
                        AdminsPermissions.roles.delete.task,
                        AdminsPermissions.roles.delete.type,
                    ) ? classes.duplicate : classes.disableDuplicate}
                >
                  Delete
                </Button>
              </span>
                        </Tooltip>
                    </DialogActions>
                </Dialog>
            </MuiThemeProvider>
        </div>
    );
};

/**
 * PropTypes definitions for the Roles component.
 *
 * This ensures that the props passed to the Roles component
 * adhere to the expected structure and data types.
 *
 * The Roles component expects:
 * - A predefined set of properties and their types defined in PropTypes.
 * - Proper validation of prop values during runtime in development mode.
 *
 * Use this to ensure type safety and avoid runtime errors related to
 * incorrect prop usage.
 */
Roles.propTypes = {
    fetchCompany: PropTypes.func,
    companyDetails: PropTypes.array,
    fetchCompanyRoles: PropTypes.func,
    rolesCompany: PropTypes.array,
    saveRole: PropTypes.func,
};

/**
 * Maps the relevant portions of the application state to the component's props.
 *
 * @param {Object} state - The current state of the redux store.
 * @returns {Object} An object containing the mapped state properties:
 * - `companyDetails`: The details of the company, derived from `state.admin.companyDetails`.
 * - `rolesCompany`: The company-specific roles, derived from `state.admin.rolesCompany`.
 * - `roles`: The general roles, derived from `state.admin.roles`.
 */
const mapStateToProps = (state) => ({
    companyDetails: state.admin.companyDetails,
    rolesCompany: state.admin.rolesCompany,
    roles: state.admin.roles,
});

/**
 * An object that maps dispatchable actions to props within a React-Redux container component.
 * This allows the component to trigger specific Redux actions directly.
 *
 * Properties:
 * - fetchCompany: A function that dispatches an action to fetch details of a company.
 * - fetchCompanyRoles: A function that dispatches an action to retrieve roles associated with a company.
 * - saveRole: A function that dispatches an action to save or update a role in the system.
 */
const mapDispatchToProps = {
    fetchCompany,
    fetchCompanyRoles,
    saveRole,
};

export default connect(mapStateToProps, mapDispatchToProps)(Roles);
